import { PlatformCatalogVersionListItemDTO, PublicationStatus } from "@bvhi/backend-api";
import { GridColDef } from "@bvhi/ui-base";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ActionCells } from "../../../../../../components/datagrid/cells/action-cells";
import { DateCell } from "../../../../../../components/datagrid/cells/date-cell";
import { PlatformCatalogPublicationStatusChip } from "../../list/components/platform-catalog-publication-status-chip";
import { CatalogVersionDetailLinkCell } from "../components/datagrid/catalog-version-detail-link-cell";
import { useCatalogVersionsSortParams } from "./use-catalog-versions-sort-params";

interface Props {
  catalogId: string;
  handleDelete: (versionId: string) => void;
}

export const useCatalogVersionsDataGridDefinitions = ({ handleDelete, catalogId }: Props) => {
  const { t } = useTranslation(["catalogs"]);
  const { isFieldSortable } = useCatalogVersionsSortParams();

  const columns: GridColDef<PlatformCatalogVersionListItemDTO>[] = useMemo(() => {
    return [
      {
        field: "name",
        headerName: t("catalogs.version.datagrid.header.name"),
        minWidth: 200,
        width: 250,
        maxWidth: 300,
        sortable: isFieldSortable("name"),
        renderCell: (params) => (
          <CatalogVersionDetailLinkCell catalogId={catalogId} catalogVersionId={params.row.id} label={params.row.name} />
        ),
      },
      {
        field: "platformCatalogVersionStatus",
        headerName: t("catalogs:catalogs.version.datagrid.header.platform.catalog.version.status"),
        minWidth: 100,
        width: 150,
        maxWidth: 200,
        sortable: isFieldSortable("platformCatalogVersionStatus"),
        renderCell: (params) => <PlatformCatalogPublicationStatusChip status={params.row.platformCatalogVersionStatus} />,
      },
      {
        field: "publishedFrom",
        headerName: t("catalogs:catalogs.version.datagrid.header.published.from"),
        minWidth: 150,
        width: 200,
        maxWidth: 250,
        sortable: isFieldSortable("publishedFrom"),
        renderCell: (params) => <DateCell date={params.row.publishedFrom} />,
      },
      {
        field: "publishedUntil",
        headerName: t("catalogs:catalogs.version.datagrid.header.published.until"),
        minWidth: 150,
        width: 200,
        maxWidth: 250,
        sortable: isFieldSortable("publishedUntil"),
        renderCell: (params) => <DateCell date={params.row.publishedUntil} fallBack={t("catalogs:catalogs.published.until.empty")} />,
      },
      {
        field: "version",
        headerName: t("catalogs:catalogs.version.datagrid.header.version"),
        minWidth: 50,
        width: 100,
        maxWidth: 150,
        sortable: isFieldSortable("version"),
      },
      {
        field: "id",
        headerName: t("catalogs:catalogs.datagrid.header.actions"),
        flex: 1,
        minWidth: 125,
        headerClassName: "datagrid-sticky-right",
        cellClassName: "datagrid-sticky-right",
        align: "right",
        headerAlign: "right",
        sortable: false,
        resizable: false,
        renderCell: (params) => (
          <ActionCells
            deleteActionDisabled={params.row.platformCatalogVersionStatus !== PublicationStatus.DRAFT}
            deletePermission="catalog:delete"
            detailParams={{ catalogId, catalogVersionId: params.row.id }}
            detailPermission="catalog:read"
            detailRoute="/app/catalogs/catalog/$catalogId/version/$catalogVersionId"
            handleDelete={handleDelete}
            id={params.row.id}
            showDeleteAction={true}
            showDetailAction={true}
          />
        ),
      },
    ];
  }, [t, isFieldSortable, catalogId, handleDelete]);

  return { columns };
};
